/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {getDefaultYearRange, range} from '../../../core/helpers/generic-helpers';
import {LoadingCounter} from '../../../core/helpers/loading-counter';
import {StateService} from '../../../core/state/state.service';
import {SelectableOptions} from '../../../models/selectable-options';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {ReportService} from '../../../services/report.service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-veh-prop-vol-compare',
    templateUrl: './veh-prop-vol-compare.component.html',
    styleUrl: './veh-prop-vol-compare.component.scss',
    standalone: false
})
export class VehPropVolCompareComponent implements OnInit {
  readonly loading = new LoadingCounter();
  selectedScenarioType: number;
  globalScenario: any;
  selectedScenarios: any[] = [];
  detailData: any[] = [];
  filteredData: any[] = [];

  startYear: number;
  endYear: number;
  yearRange: number[];

  REGION = 'REGION';
  SUBREGION = 'SUBREGION';
  PLNGMKT = 'PLNGMKT';
  MKTDIV = 'MKTDIV';
  ARCH = 'ARCH';
  PRGMCODE = 'PRGMCODE';
  BRANDFAM = 'BRANDFAM';

  regionOptions: SelectableOptions = new SelectableOptions();
  subregionOptions: SelectableOptions = new SelectableOptions();
  plngMktOptions: SelectableOptions = new SelectableOptions();
  mktDivOptions: SelectableOptions = new SelectableOptions();
  archOptions: SelectableOptions = new SelectableOptions();
  prgmCodeOptions: SelectableOptions = new SelectableOptions();
  brandfamOptions: SelectableOptions = new SelectableOptions();

  constructor(public snackBar: FabMessageService,
              public reportService: ReportService,
              public readonly excelService: ExcelExportService,
              public stateService: StateService,
              public propService: PropulsionScenarioService,
              public vehicleScenarioService: VehicleScenarioService) {
  }


  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit(): void {
    this.selectedScenarioType = 0;
  }


  /**
   * Resets selected scenarios on source change
   */
  onSourceChange(): void {
    this.selectedScenarios = [];
    this.resetAllFilterOptions();
    this.resetAllSelectedFilters();
    this.getYearRange();
  }


  /**
   * sets the scenarios returned by the event to the selected scenarios
   * @param event
   */
  selectScenario(event: any): void {
    this.selectedScenarios = event.selectedScenarios;
    if (this.selectedScenarios[0].propulsionScenario.id !== null) {
      this.getPropScenYearRange(this.selectedScenarios[0].propulsionScenario.id)
    } else {
      this.getYearRange();
    }
    this.getFiltersForPropVehComparison(this.selectedScenarios[0].vehScenId);
  }


  /**
   * Event to handle selecting a global scenario
   * @param event the event with the global and member scenarios
   */
  selectGlobalScenario(event: any) {
    this.globalScenario = event.globalScenario;
    this.selectedScenarios = event.memberScenarios;
  }


  /**
   * gets the year range for the propulsion scenario
   * @param propScenId
   */
  getPropScenYearRange(propScenId: number): void {
    this.loading.track(this.propService.getYearRange(propScenId)).subscribe({
      next: (data) => {
        this.startYear = data[0].MIN_YEAR;
        this.endYear = data[0].MAX_YEAR;
        this.yearRange = range(this.startYear, this.endYear + 1);
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    });
  }


  /**
   * Gets the year range for the reports
   */
  getYearRange(): void {
    const {yearRange, startYear, endYear} = getDefaultYearRange();
    this.yearRange = yearRange;
    this.startYear = startYear;
    this.endYear = endYear;
  }


  /**
   * Gets the filters for the report
   * @param vehScenId the scenario id to get filters for
   */
  getFiltersForPropVehComparison(vehScenId: number): void {
    this.loading.track(this.vehicleScenarioService.getFiltersForVehPropVolCompare(vehScenId,
      this.stateService.getUserGmid())).subscribe({
      next: (data) => {
        this.detailData = data;
        this.filteredData = data;
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.initializeFilterOptions();
      }
    });
  }


  /**
   * Initialize the filter options for all the filters
   */
  initializeFilterOptions(): void {
    this.regionOptions.options = this.setFilterOption(this.REGION);
    this.setCascadingFilterOptions(this.REGION);
  }


  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): string[] {
    return [...new Set(this.filteredData
    .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
    .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))];
  }


  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  setCascadingFilterOptions(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        this.subregionOptions.options = this.setFilterOption(this.SUBREGION);
        this.subregionOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.SUBREGION:
        this.plngMktOptions.options = this.setFilterOption(this.PLNGMKT);
        this.plngMktOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.PLNGMKT:
        this.mktDivOptions.options = this.setFilterOption(this.MKTDIV);
        this.mktDivOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.MKTDIV:
        this.archOptions.options = this.setFilterOption(this.ARCH);
        this.archOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.ARCH:
        this.prgmCodeOptions.options = this.setFilterOption(this.PRGMCODE);
        this.prgmCodeOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.PRGMCODE:
        this.brandfamOptions.options = this.setFilterOption(this.BRANDFAM);
        this.brandfamOptions.selectedOptions = [];
    }
  }


  /**
   * Reset the selected options from the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  cascadeResetSelectedFilter(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        if (this.subregionOptions.selectedOptions.length > 0) {
          this.subregionOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.SUBREGION:
        if (this.plngMktOptions.selectedOptions.length > 0) {
          this.plngMktOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.PLNGMKT:
        if (this.mktDivOptions.selectedOptions.length > 0) {
          this.mktDivOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.MKTDIV:
        if (this.archOptions.selectedOptions.length > 0) {
          this.archOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.ARCH:
        if (this.prgmCodeOptions.selectedOptions.length > 0) {
          this.prgmCodeOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.PRGMCODE:
        if (this.brandfamOptions.selectedOptions.length > 0) {
          this.brandfamOptions.selectedOptions = [];
        }
    }
  }


  /**
   * Event handler whenever a filter changes
   * @param filterType the filter type (which filter it is)
   */
  onFilterChange(filterType: string): void {
    // Reset the filtered data to the original data
    this.filteredData = this.detailData;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
    // Update the filtered data based off of which filters are selected
    this.applyFilter(this.REGION, this.regionOptions.selectedOptions);
    this.applyFilter(this.SUBREGION, this.subregionOptions.selectedOptions);
    this.applyFilter(this.PLNGMKT, this.plngMktOptions.selectedOptions);
    this.applyFilter(this.MKTDIV, this.mktDivOptions.selectedOptions);
    this.applyFilter(this.ARCH, this.archOptions.selectedOptions);
    this.applyFilter(this.PRGMCODE, this.prgmCodeOptions.selectedOptions);
    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
  }


  /**
   * Reset all the filter options from the filter dropdowns
   */
  resetAllFilterOptions(): void {
    this.detailData = [];
    this.regionOptions.options = [];
    this.subregionOptions.options = [];
    this.plngMktOptions.options = [];
    this.mktDivOptions.options = [];
    this.archOptions.options = [];
    this.prgmCodeOptions.options = [];
    this.brandfamOptions.options = [];
  }


  /**
   * Reset all the filter options from the filter dropdowns
   */
  resetAllSelectedFilters(): void {
    this.regionOptions.selectedOptions = [];
    this.cascadeResetSelectedFilter(this.REGION);
  }


  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilters list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilters: any): void {
    if (selectedFilters.length > 0) {
      this.filteredData = this.filteredData.filter(detailRecord => selectedFilters.includes(detailRecord[filterType].toUpperCase()));
    }
  }


  /**
   * Gets the comparison report
   */
  getReport() {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileNameScenario = this.selectedScenarios.length > 1 ? this.globalScenario.id : this.selectedScenarios[0].vehScenId + "-" + this.selectedScenarios[0].propulsionScenario.id;
    const fileName = 'VehPropVolComparison_' + fileNameScenario + '_' + currDate;
    const vehScenIdString = this.selectedScenarios.map(scenario => scenario.vehScenId).join(',');
    const propScenIdString = this.selectedScenarios.map(scenario => scenario.propulsionScenario?.id).join(',');
    if (propScenIdString.length == 0) {
      this.snackBar.displayError('Please select a vehicle scenario with a propulsion scenario', DISMISS_MSG);
      return;
    }
    this.loading.track(this.reportService.getVehPropVolComparison(vehScenIdString, propScenIdString, this.startYear,
      this.endYear, this.stateService.getUserGmid(), this.regionOptions.getSelectedOptionsString(),
      this.subregionOptions.getSelectedOptionsString(), this.plngMktOptions.getSelectedOptionsString(),
      this.archOptions.getSelectedOptionsString(), this.mktDivOptions.getSelectedOptionsString(),
      this.prgmCodeOptions.getSelectedOptionsString(), this.brandfamOptions.getSelectedOptionsString(), true)).subscribe({
      next: (response: any) => {
        this.excelService.exportToExcelFromServer(response, fileName);
      },
      error: (error: any) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.snackBar.displaySuccess('Downloaded Successfully', DISMISS_MSG);
      }
    });
  }
}
